import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { Menu } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CampoDeFicha, ModeloDeFicha } from '@/data/tipos';
import { baixarArquivo } from '@/comuns/exportar';
import { modeloParaArquivo, nomeDoArquivoDoModelo } from '@/data/modeloDeFichaEmArquivo';
import { ArquivoDeModeloModal } from './fichas/ArquivoDeModeloModal';
import { EditorDeModelo, PreviaDoModelo } from './fichas/EditorDeModelo';

export async function carregarFichas(): Promise<ModeloDeFicha[]> {
    return dados().modelosDeFicha();
}

/** O que o editor está fazendo. `null` = fechado. */
type Edicao =
    | { modelo: ModeloDeFicha | null; nomeInicial?: string; camposIniciais?: CampoDeFicha[] }
    | null;

export function Fichas() {
    const modelos = useLoaderData() as ModeloDeFicha[];
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [edicao, setEdicao] = useState<Edicao>(null);
    const [lendo, setLendo] = useState<ModeloDeFicha | null>(null);
    const [arquivando, setArquivando] = useState<ModeloDeFicha | null>(null);
    const [trocaDeArquivo, setTrocaDeArquivo] = useState(false);

    const exportar = (modelo: ModeloDeFicha) => {
        const nome = nomeDoArquivoDoModelo(modelo.nome);
        baixarArquivo(nome, 'application/json', modeloParaArquivo(modelo));
        aviso({ title: 'Modelo exportado', description: nome });
    };

    const arquivar = async (modelo: ModeloDeFicha) => {
        try {
            await dados().arquivarModeloDeFicha(modelo.id);
            aviso({ title: 'Modelo arquivado', description: `${modelo.nome} sai da lista do atendimento`, tone: 'warning' });
            setArquivando(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível arquivar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<ModeloDeFicha>[] = [
        { key: 'nome', header: 'Modelo', sortable: true, render: (m) => <strong style={{ fontWeight: 600 }}>{m.nome}</strong> },
        {
            key: 'campos', header: 'Campos', align: 'right', sortable: true,
            /* Ficha vazia aparece na lista do atendimento e não pode ser
               preenchida: ela precisa gritar na listagem, não virar um "0". */
            render: (m) => m.campos.length === 0
                ? <span style={{ color: 'var(--warning-strong)', fontWeight: 600 }}>vazia</span>
                : m.campos.length,
        },
        { key: 'versao', header: 'Versão', align: 'right', render: (m) => <Badge tone="neutral">v{m.versao}</Badge> },
        { key: 'usos', header: 'Preenchimentos', align: 'right', sortable: true, render: (m) => m.usos ?? 0 },
        {
            key: 'acoes', header: '', align: 'right', width: 56,
            render: (m) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={210}
                        onSelect={(id) => {
                            if (id === 'editar') setEdicao({ modelo: m });
                            else if (id === 'duplicar') setEdicao({ modelo: null, nomeInicial: `Cópia de ${m.nome}` });
                            else if (id === 'exportar') exportar(m);
                            else if (id === 'arquivar') setArquivando(m);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Ações de ${m.nome}`} />}
                        items={[
                            { id: 'editar', label: 'Editar campos', icon: 'pencil' },
                            { id: 'duplicar', label: 'Duplicar modelo', icon: 'copy' },
                            { id: 'exportar', label: 'Exportar JSON', icon: 'download' },
                            { id: 'divisor', label: '', divider: true },
                            { id: 'arquivar', label: 'Arquivar modelo', icon: 'archive', tone: 'danger' },
                        ]}
                    />
                </span>
            ),
        },
    ];

    const vazios = modelos.filter((m) => m.campos.length === 0);

    return (
        <>
            <PageHeader
                title="Fichas de atendimento"
                description="Modelos que o profissional preenche durante a consulta"
                actions={(
                    <>
                        <Button variant="secondary" iconLeft={<Icon name="arrow-down-up" size={15} />} onClick={() => setTrocaDeArquivo(true)}>Importar / exportar</Button>
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEdicao({ modelo: null })}>Novo modelo</Button>
                    </>
                )}
            />

            <Card padding={0}>
                {modelos.length === 0 ? (
                    <EmptyState
                        icon="clipboard-pen" title="Nenhum modelo de ficha"
                        description="Sem modelo, o atendimento não tem o que registrar no prontuário."
                        action={<Button onClick={() => setEdicao({ modelo: null })}>Criar o primeiro</Button>}
                    />
                ) : (
                    <DataTable
                        columns={colunas} rows={modelos} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        rowLabel={(m) => m.nome}
                        /* Clicar na linha abre para LEITURA. Ir direto ao editor
                           faria a consulta a um modelo virar alteração acidental. */
                        onRowClick={(m) => setLendo(m)}
                    />
                )}
            </Card>

            {vazios.length > 0 && (
                <Alert
                    tone="warning" style={{ marginTop: 16 }}
                    title={vazios.length === 1 ? 'Um modelo está sem campos' : `${vazios.length} modelos estão sem campos`}
                    action={<Button size="sm" variant="secondary" onClick={() => setEdicao({ modelo: vazios[0] ?? null })}>Adicionar campos</Button>}
                >
                    Ficha vazia aparece na lista do atendimento e não pode ser preenchida.
                </Alert>
            )}

            {lendo && (
                <Modal
                    open width={640} onClose={() => setLendo(null)}
                    title={lendo.nome}
                    description={`Versão ${lendo.versao} · ${lendo.campos.length === 0 ? 'nenhum campo' : `${lendo.campos.length} campos`} · ${lendo.usos ?? 0} preenchimentos`}
                    footer={
                        <>
                            <span style={{ marginRight: 'auto', fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)', textAlign: 'left', maxWidth: 320 }}>
                                Somente leitura. Editar o modelo não altera ficha já preenchida.
                            </span>
                            <Button variant="secondary" onClick={() => setLendo(null)}>Fechar</Button>
                            <Button
                                iconLeft={<Icon name="pencil" size={15} />}
                                onClick={() => { setEdicao({ modelo: lendo }); setLendo(null); }}
                            >
                                Editar
                            </Button>
                        </>
                    }
                >
                    <PreviaDoModelo campos={lendo.campos} />
                </Modal>
            )}

            {arquivando && (
                <Modal
                    open width={460} tone="danger" onClose={() => setArquivando(null)}
                    title={`Arquivar ${arquivando.nome}?`}
                    description="Ele sai da lista do atendimento. As fichas já preenchidas continuam no prontuário, com os campos que tinham no dia."
                    footer={
                        <>
                            <Button variant="secondary" onClick={() => setArquivando(null)}>Cancelar</Button>
                            <Button variant="danger" onClick={() => void arquivar(arquivando)}>Arquivar</Button>
                        </>
                    }
                />
            )}

            {trocaDeArquivo && (
                <ArquivoDeModeloModal
                    modelos={modelos} onFechar={() => setTrocaDeArquivo(false)} onExportar={exportar}
                    onImportar={(lido) => {
                        setTrocaDeArquivo(false);
                        setEdicao({ modelo: null, nomeInicial: lido.nome, camposIniciais: lido.campos });
                    }}
                />
            )}

            <EditorDeModelo
                open={edicao !== null}
                modelo={edicao?.modelo ?? null}
                nomeInicial={edicao?.nomeInicial}
                camposIniciais={edicao?.camposIniciais}
                onClose={() => setEdicao(null)}
                onSalvo={() => revalidator.revalidate()}
            />
        </>
    );
}
